import React from 'react';
import c from 'classnames';
import config from 'react-global-configuration';
import { useTranslation } from 'react-i18next';

import { SearchConfigure, SearchkitWrapper } from '@uncinc/react-searchkit';
import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import {
  SEARCHKIT_HOST_URL,
  SEARCHKIT_INDEX_PROMOTED_CONTENT_THEMES,
  SEARCHKIT_PROMOTED_CONTENT_THEMES_FACET_SUBJECT,
  SEARCHKIT_PROMOTED_CONTENT_THEMES_HITS_PER_PAGE,
} from '../../config/constants';

import {
  facetAttributes,
  resultAttributes,
  sorting,
} from './config';

// Subcomponents.
import AdvancedFacets from './AdvancedFacets';
import Facets from './Facets';
import PillarFacet from './PillarFacet';
import Results from './Results';
import Stats from './Stats';

interface SearchPromotedContentThemesSubcomponents {
  AdvancedFacets: typeof AdvancedFacets;
  Facets: typeof Facets;
  PillarFacet: typeof PillarFacet;
  Results: typeof Results;
  Stats: typeof Stats;
}

export interface SearchPromotedContentThemesProps extends Omit<SearchkitWrapperProps, 'connection' | 'indexName' | 'searchSettings'> {
  theme?: number;
  showSubjects?: boolean;
}

// eslint-disable-next-line max-len
const SearchPromotedContentThemes: React.FC<SearchPromotedContentThemesProps> & SearchPromotedContentThemesSubcomponents = (props) => {
  const {
    className,
    theme,
    showSubjects = false,
    children,
    ...restProps
  } = props;

  const classNames = c('SearchPromotedContentThemes', className);
  const { t } = useTranslation();

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      connection={{ host: SEARCHKIT_HOST_URL }}
      indexName={SEARCHKIT_INDEX_PROMOTED_CONTENT_THEMES}
      hitsPerPage={SEARCHKIT_PROMOTED_CONTENT_THEMES_HITS_PER_PAGE}
      routing
      searchSettings={{
        search_attributes: [],
        result_attributes: resultAttributes,
        facet_attributes: facetAttributes,
        sorting,
        query_rules: [
          {
            id: '1',
            conditions: [
              [
                {
                  context: 'context',
                  value: ['hide_subject_facet'],
                },
              ],
            ],
            actions: [
              {
                action: 'RenderFacetsOrder',
                facetAttributesOrder: facetAttributes
                  .map((facetAttribute) => (
                    typeof facetAttribute === 'object' ? facetAttribute.attribute : facetAttribute
                  ))
                  .filter((facetAttribute) => (
                    facetAttribute !== SEARCHKIT_PROMOTED_CONTENT_THEMES_FACET_SUBJECT
                  )),
              },
            ],
          },
        ],
      }}
      requestOptions={{
        // @ts-expect-error A 'must' query if not part of the QueryDslQueryContainer
        // typing, but the query is valid nonetheless. This should be fixed in
        // @elastic/elasticsearch/lib/api/types.
        getBaseFilters: () => [
          {
            bool: {
              must: [
                {
                  term: {
                    field_domain_access: {
                      value: config.get('CURRENT_DOMAIN_DRUPAL_IDENTIFIER') as string,
                    },
                  },
                },
                {
                  bool: {
                    should: [
                      {
                        term: {
                          field_show_in_dossier: {
                            value: true,
                          },
                        },
                      },
                      {
                        bool: {
                          must_not: {
                            exists: {
                              field: 'field_show_in_dossier',
                            },
                          },
                        },
                      },
                    ],
                    minimum_should_match: 1,
                  },
                },
              ],
              must_not: [
                {
                  term: {
                    article_type: {
                      value: t('tags.nieuwsbericht'),
                    },
                  },
                },
              ],
            },
          },
          ...(theme
            ? [
              {
                bool: {
                  must: {
                    term: {
                      theme: {
                        value: theme,
                      },
                    },
                  },
                },
              },
            ]
            : []),
        ],
      }}
    >
      <SearchConfigure ruleContexts={[!showSubjects ? 'hide_subject_facet' : '']} />
      {children}
    </SearchkitWrapper>
  );
};

SearchPromotedContentThemes.AdvancedFacets = AdvancedFacets;
SearchPromotedContentThemes.Facets = Facets;
SearchPromotedContentThemes.PillarFacet = PillarFacet;
SearchPromotedContentThemes.Results = Results;
SearchPromotedContentThemes.Stats = Stats;

export default SearchPromotedContentThemes;
